"use client";
import React, { useState, useEffect } from 'react'
import Image from "next/image";
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import type { Swiper as SwiperType } from 'swiper';
import "swiper/css";
import "swiper/css/pagination";

export default function EcoCompliance() {
  const t = useTranslations('plsyy');
  const [activeIndex, setActiveIndex] = useState(0);

  // 证书数据
  const certificates = [
    {
      id: 1,
      image: "/image/plsyy/about/c1.webp",
      title: t('Certificate-1')
    },
    {
      id: 2,
      image: "/image/plsyy/about/c2.webp",
      title: t('Certificate-2')
    },
    {
      id: 3,
      image: "/image/plsyy/about/c3.webp",
      title: t('Certificate-3')
    },
    {
      id: 4,
      image: "/image/plsyy/about/c4.webp",
      title: t('Certificate-4')
    },
    {
      id: 5,
      image: "/image/plsyy/about/c5.webp",
      title: t('Certificate-5')
    },
    {
      id: 6,
      image: "/image/plsyy/about/c6.webp",
      title: t('Certificate-6')
    },
    {
      id: 7,
      image: "/image/plsyy/about/c7.webp",
      title: t('Certificate-7')
    },
    {
      id: 8,
      image: "/image/plsyy/about/c8.webp",
      title: t('Certificate-8')
    },
    {
      id: 9,
      image: "/image/plsyy/about/c9.webp",
      title: t('Certificate-9')
    }
  ];

  // 添加错落突出的样式
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .certificates-swiper-3d {
        overflow: visible !important;
        padding: 20px 0 40px 0 !important;
      }
      @media (min-width: 640px) {
        .certificates-swiper-3d {
          padding: 40px 0 60px 0 !important;
        }
      }
      @media (min-width: 1024px) {
        .certificates-swiper-3d {
          padding: 60px 0 80px 0 !important;
        }
      }
      .certificates-swiper-3d .swiper-wrapper {
        align-items: center;
        display: flex;
      }
      .certificates-swiper-3d .swiper-slide {
        transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        opacity: 1;
        transform: scale(0.85) translateY(20px) rotateY(10deg);
        z-index: 1;
      }
      @media (min-width: 640px) {
        .certificates-swiper-3d .swiper-slide {
          transform: scale(0.7) translateY(30px) rotateY(15deg);
        }
      }
      .certificates-swiper-3d .swiper-slide-active {
        opacity: 1 !important;
        transform: scale(1) translateY(0) rotateY(0deg) !important;
        z-index: 10 !important;
      }
      .certificates-swiper-3d .swiper-slide-prev {
        opacity: 1;
        transform: scale(0.9) translateY(15px) rotateY(8deg);
        z-index: 3;
      }
      @media (min-width: 640px) {
        .certificates-swiper-3d .swiper-slide-prev {
          transform: scale(0.85) translateY(20px) rotateY(10deg);
        }
      }
      .certificates-swiper-3d .swiper-slide-next {
        opacity: 1;
        transform: scale(0.9) translateY(15px) rotateY(-8deg);
        z-index: 3;
      }
      @media (min-width: 640px) {
        .certificates-swiper-3d .swiper-slide-next {
          transform: scale(0.85) translateY(20px) rotateY(-10deg);
        }
      }
      .certificates-swiper-3d .swiper-slide-prev-prev {
        opacity: 0.6;
        transform: scale(0.85) translateY(20px) rotateY(10deg);
        z-index: 1;
      }
      @media (min-width: 640px) {
        .certificates-swiper-3d .swiper-slide-prev-prev {
          opacity: 1;
          transform: scale(0.7) translateY(30px) rotateY(15deg);
        }
      }
      .certificates-swiper-3d .swiper-slide-next-next {
        opacity: 0.6;
        transform: scale(0.85) translateY(20px) rotateY(-10deg);
        z-index: 1;
      }
      @media (min-width: 640px) {
        .certificates-swiper-3d .swiper-slide-next-next {
          opacity: 1;
          transform: scale(0.7) translateY(30px) rotateY(-15deg);
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <section id="certificates" className='py-16 max-md:py-8 scroll-mt-40'>
      <div className="container mx-auto">
				{/* 标题区域 */}
				<div className="pb-4 sm:pb-6 md:pb-8 px-4 text-center">
					<h2 className="text-2xl sm:text-3xl xl:text-4xl font-bold text-black leading-tight text-center">
						{t('Our-Certificates')}
					</h2>
				</div>

        {/* Certificates 轮播 - 错落突出效果 */}
        <motion.div 
          className="container mx-auto relative max-md:px-2"
          initial={{ opacity: 0, y: 80 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ 
            duration: 1,
            delay: 0.4
          }}
        >
          <div className="relative w-full max-w-full overflow-hidden">
            <Swiper
              modules={[Autoplay, Pagination]}
              spaceBetween={20}
              slidesPerView={1.2}
              centeredSlides={true}
              loop={true}
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
              }}
              pagination={{
                clickable: true,
                dynamicBullets: true,
              }}
              onSlideChange={(swiper: SwiperType) => {
                setActiveIndex(swiper.realIndex);
              }}
              breakpoints={{
                375: {
                  slidesPerView: 1.3,
                  spaceBetween: 20,
                },
                414: {
                  slidesPerView: 1.4,
                  spaceBetween: 20,
                },
                640: {
                  slidesPerView: 1.8,
                  spaceBetween: 20,
                },
                768: {
                  slidesPerView: 2.5,
                  spaceBetween: -10,
                },
                1024: {
                  slidesPerView: 3.5,
                  spaceBetween: -10,
                },
                1280: {
                  slidesPerView: 5,
                  spaceBetween: -5,
                },
              }}
              className="certificates-swiper-3d"
            >
              {certificates.map((cert) => (
                <SwiperSlide key={cert.id}>
                  <div className="relative w-[240px] h-[320px] max-md:w-[240px] max-md:h-[320px] sm:w-[280px] sm:h-[380px] md:w-[300px] md:h-[400px] lg:w-[320px] lg:h-[440px] xl:w-[360px] xl:h-[480px] mx-auto">
                    <div className="relative w-full h-full bg-white rounded-lg shadow-xl overflow-hidden">
                      <Image
                        src={cert.image}
                        alt={cert.title}
                        fill
                        quality={100}
                        priority={false}
                        sizes="(max-width: 640px) 240px, (max-width: 768px) 280px, (max-width: 1024px) 300px, (max-width: 1280px) 320px, 360px"
                        className="object-cover"
                        unoptimized={false}
                      />
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            
            {/* 证书名称显示 - 在正下方 */}
            <div className="text-center mt-4 max-md:mt-4 sm:mt-6 md:mt-8 px-4">
              <motion.h3
                key={activeIndex}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="text-base max-md:text-base sm:text-lg md:text-xl font-semibold text-gray-900"
              >
                {certificates[activeIndex]?.title}
              </motion.h3>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
